/**
 * Step 3: Investment Holdings & Additional Pensions
 * Captures SIPP and ISA holdings plus DB/workplace pensions for each person.
 * Ticker lookup auto-fills fund name, asset class, and OCF from LSE data.
 */

import {
  useForm, useFieldArray, Controller,
  type Control, type Path, type FieldValues, type FieldErrors, type UseFormRegister, type UseFormWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useMemo, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { z } from 'zod';
import {
  Step3FormData, HoldingData, AdditionalPensionData, ContributionPersonData, BondDetailsSchema,
  type AllStepFormData, type PersonData,
} from '../../types/wizard';
import type { DbIndexation } from '../../types/projection';
import { formatYearAge } from '../../utils/yearAgeLabel';
import { ContributionsSection } from './ContributionsSection';
import { formatNumber, unformatNumber, planYearToCalendar } from '../../utils/formatters';
import { lookupFundTicker, searchFunds as searchFundsApi, getLivePrice, FundMetadataResponse } from '../../services/planApi';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP3_TOUR } from '../TourGuide/tourSteps';
import HoldingsTable from './HoldingsTable';
import HoldingEditDrawer from './HoldingEditDrawer';
import './Step3Holdings.css';
import { formatOcf } from './holdingsFormat';
import {
  DEFAULT_COMMUTATION_FACTOR, adjustedDbPensionAmount, ageInPlanYear, maxCommutablePension,
} from '../../utils/dbPensionMath';
import { isFeatureEnabled } from '../../config/features';
import {
  DB_INDEXATION_LABELS, DB_PENSION_PRESETS, applyPreset, describePresetFill, findPreset,
} from '../../constants/dbPensionPresets';

// Holding schema used for validation (BondDetailsSchema imported from wizard.ts)
const HoldingSchema = z.object({
  holdingId: z.string(),
  accountType: z.enum(['SIPP', 'ISA', 'BROKERAGE']),
  etfName: z.string().min(1, 'Fund name is required'),
  ticker: z.string().optional(),
  assetClass: z.enum(['EQUITY', 'BOND', 'MIXED', 'OTHER']),
  currentValue: z.number().positive('Value must be greater than 0'),
  ocfPct: z.number().min(0).max(0.1).optional(),
  bondDetails: BondDetailsSchema.optional(),
  costBasis: z.number().min(0).optional(),
  quantity: z.number().positive().optional(),
  unitPrice: z.number().positive().optional(),
  quantityTracked: z.boolean().optional(),
});

/** Optional numeric input: an emptied field (NaN / '' / null) is "not set", never an error. */
const optionalPensionNumber = (schema: z.ZodNumber) =>
  z.preprocess(
    (v) => (v === '' || v === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v),
    schema.optional(),
  );

const DB_INDEXATION_VALUES = ['NONE', 'CPI', 'CPI_CAPPED_2_5', 'CPI_CAPPED_5', 'FIXED'] as const;

const AdditionalPensionSchema = z.object({
  pensionId: z.string(),
  name: z.string().min(1, 'Pension name is required'),
  annualAmount: z.number().positive('Amount must be greater than 0').max(10_000_000, 'Cannot exceed £10M'),
  startYear: z.number().int().min(1, 'Start year must be at least 1').max(100, 'Cannot exceed 100'),
  inflationLinked: z.boolean(),
  indexation: z.enum(DB_INDEXATION_VALUES).optional(),
  fixedIndexationPct: optionalPensionNumber(z.number().min(0).max(20, 'Cannot exceed 20% a year')),
  normalPensionAge: optionalPensionNumber(z.number().int().min(40, 'Must be 40 or more').max(80, 'Must be 80 or less')),
  earlyReductionPctPerYear: optionalPensionNumber(z.number().min(0).max(20)),
  lateIncreasePctPerYear: optionalPensionNumber(z.number().min(0).max(20)),
  automaticLumpSum: optionalPensionNumber(z.number().min(0).max(10_000_000)),
  commutedAnnual: optionalPensionNumber(z.number().min(0).max(10_000_000)),
  commutationFactor: optionalPensionNumber(z.number().min(1, 'Must be at least 1').max(30, 'Must be 30 or less')),
  survivorPct: optionalPensionNumber(z.number().min(0).max(100, 'Must be 100% or less')),
  schemePreset: z.string().optional(),
});

/** Member's age in a pension's start year (undefined when the person's dates are unusable). */
type StartAgeResolver = (personIndex: number, startYear: number) => number | undefined;

/** Per-person pension schema: the HMRC commutation cap needs the member's start age. */
function makePensionSchema(personIndex: number, resolveStartAge: StartAgeResolver) {
  return AdditionalPensionSchema.superRefine((p, ctx) => {
    if (p.indexation === 'FIXED' && p.fixedIndexationPct == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fixedIndexationPct'], message: 'Enter the fixed increase' });
    }
    if ((p.commutedAnnual ?? 0) > 0) {
      const adjusted = adjustedDbPensionAmount(
        p.annualAmount, p.normalPensionAge, resolveStartAge(personIndex, p.startYear),
        p.earlyReductionPctPerYear, p.lateIncreasePctPerYear,
      );
      const cap = maxCommutablePension(adjusted, p.commutationFactor ?? DEFAULT_COMMUTATION_FACTOR);
      if ((p.commutedAnnual ?? 0) > cap + 0.005) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['commutedAnnual'], message: 'More than the HMRC maximum can be given up' });
      }
    }
  });
}

/** A draft saved before indexation existed carries only `inflationLinked`. */
function legacyIndexation(p: AdditionalPensionData): AdditionalPensionData['indexation'] {
  return p.indexation ?? (p.inflationLinked ? 'CPI' : 'NONE');
}

/**
 * What the step emits: `inflationLinked` kept in step with `indexation`, and
 * every optional number omitted (never null, never 0-as-unset) so the
 * server sees exactly the fields the user set.
 */
function normalisePensionForSave(p: AdditionalPensionData, planType: 'SINGLE' | 'JOINT'): AdditionalPensionData {
  const indexation = legacyIndexation(p);
  const optional = (v: number | undefined): number | undefined =>
    (v == null || Number.isNaN(v) || v <= 0) ? undefined : v;
  const out: AdditionalPensionData = {
    pensionId: p.pensionId,
    name: p.name,
    annualAmount: p.annualAmount,
    startYear: p.startYear,
    inflationLinked: indexation !== 'NONE',
    indexation,
  };
  if (indexation === 'FIXED' && optional(p.fixedIndexationPct) != null) out.fixedIndexationPct = p.fixedIndexationPct;
  if (optional(p.normalPensionAge) != null) {
    out.normalPensionAge = p.normalPensionAge;
    if (optional(p.earlyReductionPctPerYear) != null) out.earlyReductionPctPerYear = p.earlyReductionPctPerYear;
    if (optional(p.lateIncreasePctPerYear) != null) out.lateIncreasePctPerYear = p.lateIncreasePctPerYear;
  }
  if (optional(p.automaticLumpSum) != null) out.automaticLumpSum = p.automaticLumpSum;
  if (optional(p.commutedAnnual) != null) {
    out.commutedAnnual = p.commutedAnnual;
    if (optional(p.commutationFactor) != null) out.commutationFactor = p.commutationFactor;
  }
  if (planType === 'JOINT' && optional(p.survivorPct) != null) out.survivorPct = p.survivorPct;
  if (p.schemePreset) out.schemePreset = p.schemePreset;
  return out;
}

// Form data shape includes both holdings and additional pensions
export interface Step3InternalFormData {
  holdings: Record<string, HoldingData[]>;
  additionalPensions: Record<string, AdditionalPensionData[]>;
}

// Create plan-type-specific schema
function createStep3Schema(planType: 'SINGLE' | 'JOINT', resolveStartAge: StartAgeResolver) {
  if (planType === 'JOINT') {
    return z.object({
      holdings: z.object({
        person0: z.array(HoldingSchema),
        person1: z.array(HoldingSchema),
      }),
      additionalPensions: z.object({
        person0: z.array(makePensionSchema(0, resolveStartAge)),
        person1: z.array(makePensionSchema(1, resolveStartAge)),
      }).optional(),
    });
  }
  // SINGLE plan - only validate person0, ignore person1
  return z.object({
    holdings: z.object({
      person0: z.array(HoldingSchema),
    }).passthrough(),
    additionalPensions: z.object({
      person0: z.array(makePensionSchema(0, resolveStartAge)),
    }).passthrough().optional(),
  });
}

/** Map fund API asset class to wizard holding asset class */
function mapFundAssetClass(fundClass: FundMetadataResponse['assetClass']): 'EQUITY' | 'BOND' | 'MIXED' | 'OTHER' {
  switch (fundClass) {
    case 'EQUITY': return 'EQUITY';
    case 'BOND': return 'BOND';
    case 'MIXED': return 'MIXED';
    default: return 'OTHER'; // COMMODITY, OTHER
  }
}

/** Reusable currency input wrapped in Controller to reduce nesting depth */
export function CurrencyInput<TFieldValues extends FieldValues>({
  name,
  control: ctrl,
  onBlur: onFieldBlur,
  onValueChange,
  placeholder = '0',
  showZeroAsEmpty = false,
  decimals = 0,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: Readonly<{
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  onBlur?: () => void;
  /** Fires with the parsed number on every change — used to live-derive a sibling field. */
  onValueChange?: (value: number) => void;
  placeholder?: string;
  showZeroAsEmpty?: boolean;
  decimals?: number;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}>) {
  return (
    <Controller
      name={name}
      control={ctrl}
      render={({ field }) => {
        const hasValue = field.value !== undefined && field.value !== null && !Number.isNaN(field.value);
        const displayValue = hasValue && !(showZeroAsEmpty && field.value === 0)
          ? formatNumber(field.value, decimals) : '';
        return (
          <input
            id={id}
            type="text"
            className="form-input form-input-sm"
            placeholder={placeholder}
            value={displayValue}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            onChange={(e) => {
              const rawValue = unformatNumber(e.target.value);
              const numValue = Number.parseFloat(rawValue);
              const parsed = Number.isNaN(numValue) ? 0 : numValue;
              field.onChange(parsed);
              onValueChange?.(parsed);
            }}
            onBlur={() => {
              field.onBlur();
              onFieldBlur?.();
            }}
          />
        );
      }}
    />
  );
}

export type TickerLookupStatus = 'idle' | 'loading' | 'found' | 'not-found' | 'error';

export interface TickerLookupState {
  status: TickerLookupStatus;
  fundName?: string;
}

/** Autocomplete dropdown for ticker input */
export function TickerAutocomplete({
  value,
  onChange,
  onSelect,
  onBlur,
  lookupStatus,
}: Readonly<{
  value: string;
  onChange: (value: string) => void;
  onSelect: (fund: FundMetadataResponse) => void;
  onBlur: () => void;
  lookupStatus?: TickerLookupStatus;
}>) {
  const [suggestions, setSuggestions] = useState<FundMetadataResponse[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, []);

  const handleInputChange = useCallback((inputValue: string) => {
    onChange(inputValue);
    setActiveIndex(-1);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (!inputValue || inputValue.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      try {
        const results = await searchFundsApi(inputValue.trim());
        setSuggestions(results);
        setShowDropdown(results.length > 0);
      } catch (error) {
        console.warn('Fund search failed:', error instanceof Error ? error.message : error);
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);
  }, [onChange]);

  const handleSelect = useCallback((fund: FundMetadataResponse) => {
    setShowDropdown(false);
    setSuggestions([]);
    onSelect(fund);
  }, [onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        // Stop here too, same reason as Escape below — while the dropdown is
        // open this keystroke belongs entirely to the autocomplete, not to
        // the wrapping form's own keydown handler.
        e.preventDefault();
        e.stopPropagation();
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSelect(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        // Stop here — otherwise this also bubbles to HoldingEditDrawer's
        // document-level Escape listener and closes the whole drawer.
        e.stopPropagation();
        setShowDropdown(false);
        break;
    }
  }, [showDropdown, suggestions, activeIndex, handleSelect]);

  const statusClass = lookupStatus === 'found' ? 'ticker-found'
    : lookupStatus === 'not-found' ? 'ticker-not-found'
    : lookupStatus === 'error' ? 'ticker-error'
    : '';

  return (
    <div className="ticker-autocomplete" ref={wrapperRef}>
      <div className="ticker-input-wrapper">
        <input
          type="text"
          className={`form-input form-input-sm ${statusClass}`}
          placeholder="e.g., VWRL"
          value={value}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Delay to allow click on dropdown item
            setTimeout(() => {
              onBlur();
            }, 200);
          }}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          autoComplete="off"
        />
        {lookupStatus === 'loading' && (
          <span className="ticker-spinner" aria-label="Looking up ticker" />
        )}
      </div>
      {showDropdown && suggestions.length > 0 && (
        <ul className="ticker-dropdown" role="listbox">
          {suggestions.slice(0, 8).map((fund, i) => (
            <li
              key={fund.ticker}
              className={`ticker-dropdown-item ${i === activeIndex ? 'active' : ''}`}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={() => handleSelect(fund)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="ticker-dropdown-ticker">{fund.ticker}</span>
              <span className="ticker-dropdown-name">{fund.name}</span>
              {fund.exchange && <span className="ticker-dropdown-exchange">{fund.exchange}</span>}
              <span className={`ticker-dropdown-badge badge-${fund.assetClass.toLowerCase()}`}>
                {fund.assetClass}
              </span>
              {fund.ocfPct != null && fund.ocfPct > 0 && (
                <span className="ticker-dropdown-ocf">{formatOcf(fund.ocfPct)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {lookupStatus === 'not-found' && !(showDropdown && suggestions.length > 0) && (
        <span className="ticker-hint">Not found - enter details manually</span>
      )}
      {lookupStatus === 'error' && !(showDropdown && suggestions.length > 0) && (
        <span className="ticker-hint ticker-error-hint">Lookup failed - enter details manually</span>
      )}
    </div>
  );
}

/**
 * RHF's FieldErrors type cannot describe errors under an index signature
 * (`holdings: Record<string, HoldingData[]>`), so this names the shape the
 * markup reads. Previously every access was cast to `any`, which also hid
 * typos in the field names.
 */
type PersonKeyedFieldErrors = Record<string, Array<Record<string, { message?: string } | undefined>> | undefined>;

const gbp = (n: number): string => `£${Math.round(n).toLocaleString('en-GB')}`;
/** A number input's value: NaN (emptied field) reads as unset. */
const numOrUndefined = (v: number | undefined): number | undefined => (v == null || Number.isNaN(v) ? undefined : v);

interface PensionDetailFieldsProps {
  personIndex: number;
  pensionIndex: number;
  planType: 'SINGLE' | 'JOINT';
  person: PersonData | undefined;
  planAsOfDate: string | undefined;
  register: UseFormRegister<Step3InternalFormData>;
  control: Control<Step3InternalFormData>;
  watch: UseFormWatch<Step3InternalFormData>;
  fieldErrors: Record<string, { message?: string } | undefined> | undefined;
  onFieldBlur: () => void;
  presetNote: string;
  /** Opened once when a preset fills the fields, so they can be checked. */
  defaultMoreOptionsOpen: boolean;
}

/**
 * Indexation select plus the "More options" disclosure of a pension card:
 * normal pension age with the computed early/late caption, lump sums with the
 * HMRC commutation limit, the early/late factors and (JOINT only) the survivor
 * pension. Captions are computed live from the watched field values.
 */
function PensionDetailFields({
  personIndex, pensionIndex, planType, person, planAsOfDate,
  register, control, watch, fieldErrors, onFieldBlur, presetNote, defaultMoreOptionsOpen,
}: Readonly<PensionDetailFieldsProps>) {
  const base = `additionalPensions.person${personIndex}.${pensionIndex}` as const;
  const id = (suffix: string) => `pension-${suffix}-${personIndex}-${pensionIndex}`;
  const annualAmount = watch(`${base}.annualAmount`) || 0;
  const startYear = watch(`${base}.startYear`);
  const indexation = watch(`${base}.indexation`) ?? 'CPI';
  const normalPensionAge = numOrUndefined(watch(`${base}.normalPensionAge`));
  const earlyReduction = numOrUndefined(watch(`${base}.earlyReductionPctPerYear`));
  const lateIncrease = numOrUndefined(watch(`${base}.lateIncreasePctPerYear`));
  const commutedAnnual = watch(`${base}.commutedAnnual`) || 0;
  const commutationFactor = numOrUndefined(watch(`${base}.commutationFactor`)) ?? DEFAULT_COMMUTATION_FACTOR;
  const automaticLumpSum = watch(`${base}.automaticLumpSum`) || 0;

  const startAge = person?.dateOfBirth && planAsOfDate && Number.isInteger(startYear)
    ? ageInPlanYear(person.dateOfBirth, planAsOfDate, startYear)
    : undefined;
  const adjusted = adjustedDbPensionAmount(annualAmount, normalPensionAge, startAge, earlyReduction, lateIncrease);
  const maxCommutable = maxCommutablePension(adjusted, commutationFactor);
  const taxFreeCash = automaticLumpSum + commutedAnnual * commutationFactor;

  let adjustmentCaption: string | null = null;
  if (normalPensionAge != null && startAge != null && annualAmount > 0) {
    const yearsFromNpa = startAge - normalPensionAge;
    const pct = Math.round(Math.abs(adjusted / annualAmount - 1) * 100);
    if (yearsFromNpa === 0) {
      adjustmentCaption = `Starting at ${startAge} is your normal pension age: no adjustment.`;
    } else if (yearsFromNpa < 0) {
      adjustmentCaption = `Starting at ${startAge} is ${-yearsFromNpa} ${-yearsFromNpa === 1 ? 'year' : 'years'} early: amount reduced by ${pct}% to ${gbp(adjusted)}.`;
    } else {
      adjustmentCaption = `Starting at ${startAge} is ${yearsFromNpa} ${yearsFromNpa === 1 ? 'year' : 'years'} late: amount increased by ${pct}% to ${gbp(adjusted)}.`;
    }
  }

  const numberField = (
    suffix: string,
    field: 'fixedIndexationPct' | 'normalPensionAge' | 'earlyReductionPctPerYear' | 'lateIncreasePctPerYear' | 'commutationFactor' | 'survivorPct',
    label: string,
    attrs: { min: number; max: number; step?: number },
    caption?: ReactNode,
  ) => (
    <div className="pension-field">
      <label htmlFor={id(suffix)} className="pension-label">{label}</label>
      <input
        id={id(suffix)}
        type="number"
        className={`form-input form-input-sm ${fieldErrors?.[field] ? 'error' : ''}`}
        min={attrs.min}
        max={attrs.max}
        step={attrs.step ?? 1}
        {...register(`${base}.${field}`, { valueAsNumber: true })}
        onBlur={onFieldBlur}
      />
      {fieldErrors?.[field]?.message && <span className="field-error-inline">{fieldErrors[field]?.message}</span>}
      {caption}
    </div>
  );

  return (
    <div className="pension-field pension-field-wide">
      <div className="pension-more-grid">
        <div className="pension-field">
          <label htmlFor={id('indexation')} className="pension-label">Increases in payment</label>
          <select
            id={id('indexation')}
            className="form-input form-input-sm"
            {...register(`${base}.indexation`)}
            onChange={(e) => {
              void register(`${base}.indexation`).onChange(e);
              onFieldBlur();
            }}
          >
            {(Object.keys(DB_INDEXATION_LABELS) as DbIndexation[]).map(k => (
              <option key={k} value={k}>{DB_INDEXATION_LABELS[k]}</option>
            ))}
          </select>
        </div>
        {indexation === 'FIXED' && numberField('fixed-pct', 'fixedIndexationPct', 'Fixed increase (% a year)', { min: 0, max: 20, step: 0.1 })}
        <p className="pension-help">
          Enter the pension as your statement shows it at its payment date, in today&apos;s money — it is not
          revalued between now and then. One increase rule applies to the whole pension, including any
          Guaranteed Minimum Pension.
        </p>
      </div>
      {presetNote && <p className="pension-preset-note" role="status">{presetNote}</p>}
      <details className="pension-more-options" open={defaultMoreOptionsOpen}>
        <summary>More options</summary>
        <div className="pension-more-grid">
          {numberField('npa', 'normalPensionAge', 'Normal pension age', { min: 40, max: 80 }, (
            <>
              <span className="field-context-hint">
                The age your scheme pays in full. Starting before it reduces the pension, after it increases it.
              </span>
              {adjustmentCaption && <span className="field-context-hint" data-testid={id('npa-caption')}>{adjustmentCaption}</span>}
              <span className="field-context-hint">Approximate; use your scheme&apos;s own quote if you are close to a birthday.</span>
            </>
          ))}
          {numberField('early', 'earlyReductionPctPerYear', 'Early reduction (% a year)', { min: 0, max: 20, step: 0.1 }, (
            <span className="field-context-hint">Applied in whole years, so a part-year is rounded.</span>
          ))}
          {numberField('late', 'lateIncreasePctPerYear', 'Late increase (% a year)', { min: 0, max: 20, step: 0.1 })}
          <div className="pension-field">
            <label htmlFor={id('auto-lump')} className="pension-label">Automatic lump sum (£)</label>
            <CurrencyInput id={id('auto-lump')} name={`${base}.automaticLumpSum`} control={control} onBlur={onFieldBlur} showZeroAsEmpty />
            <span className="field-context-hint">Paid with the first pension, e.g. 3× pension in the 1995 NHS scheme.</span>
          </div>
          <div className="pension-field">
            <label htmlFor={id('commute')} className="pension-label">Pension given up for cash (£ a year)</label>
            <CurrencyInput id={id('commute')} name={`${base}.commutedAnnual`} control={control} onBlur={onFieldBlur} showZeroAsEmpty />
            {fieldErrors?.commutedAnnual?.message && <span className="field-error-inline">{fieldErrors.commutedAnnual.message}</span>}
            <span className="field-context-hint" data-testid={id('commute-caption')}>
              {gbp(taxFreeCash)} tax-free cash. HMRC limits the tax-free cash you can take, which caps the
              pension you can give up at {gbp(maxCommutable)} a year.
            </span>
          </div>
          {numberField('factor', 'commutationFactor', 'Cash per £1 of pension given up', { min: 1, max: 30, step: 0.5 })}
          {planType === 'JOINT' && numberField('survivor', 'survivorPct', 'Survivor pension (%)', { min: 0, max: 100, step: 0.05 }, (
            <span className="field-context-hint">Paid to your partner after your death, from the year after.</span>
          ))}
        </div>
      </details>
    </div>
  );
}

export interface Step3Props {
  /**
   * The wizard hands every step the whole accumulated form data, not just its
   * own slice — this step reads planType and persons from steps 1 and 2.
   */
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step3FormData) => void;
  onSave: (data: Step3FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

/**
 * Uncontrolled OCF input — lets the user type freely without reformatting
 * every keystroke. Syncs the parsed decimal value to the form on blur.
 * Accepts percentage values (e.g. 0.22 → 0.22%, 5.00 → 5%).
 */
export function OcfInput({ value, onChange, onBlur }: Readonly<{
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  onBlur: () => void;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const externalValueRef = useRef(value);

  // Sync external value changes (e.g. ticker auto-fill) into the DOM
  useEffect(() => {
    if (value !== externalValueRef.current && inputRef.current) {
      externalValueRef.current = value;
      inputRef.current.value = value != null && value > 0
        ? (value * 100).toFixed(2)
        : '';
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    const text = inputRef.current?.value.replace('%', '').trim() || '';
    if (text === '') {
      onChange(undefined);
      onBlur();
      return;
    }
    const pct = Number.parseFloat(text);
    if (!Number.isNaN(pct) && pct >= 0) {
      const decimal = pct / 100;
      onChange(decimal);
      // Re-format display after sync
      if (inputRef.current) {
        inputRef.current.value = (decimal * 100).toFixed(2);
      }
    } else {
      // Restore previous value on invalid input
      if (inputRef.current) {
        inputRef.current.value = value != null && value > 0
          ? (value * 100).toFixed(2)
          : '';
      }
    }
    onBlur();
  }, [onChange, onBlur, value]);

  return (
    <input
      type="text"
      ref={inputRef}
      className="form-input form-input-sm form-input-ocf"
      placeholder="e.g., 0.22"
      title="Annual fund charge as a percentage — e.g. 0.22 for 0.22%, 5 for 5%."
      defaultValue={value != null && value > 0 ? (value * 100).toFixed(2) : ''}
      onBlur={handleBlur}
    />
  );
}

function Step3Holdings({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step3Props>) {
  const planType = initialData?.planType || 'SINGLE';
  const persons = useMemo(() => initialData?.persons ?? [], [initialData?.persons]);
  const numPersons = planType === 'JOINT' ? 2 : 1;

  // Active person tab (for JOINT plans)
  const [activePersonIndex, setActivePersonIndex] = useState(0);
  // Collapsible pension sections
  const [pensionExpanded, setPensionExpanded] = useState<Record<number, boolean>>({ 0: true, 1: true });
  // Contributions state (managed outside react-hook-form)
  const [contributions, setContributions] = useState<Record<string, ContributionPersonData>>(
    () => initialData?.contributions || {}
  );
  // Ticker lookup state per holding (keyed by "personIndex-holdingIndex")
  const [tickerLookups, setTickerLookups] = useState<Record<string, TickerLookupState>>({});
  // Debounce timer refs
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Per-field monotonic token for auto-price/lookup requests (keyed by fieldId).
  // A later request bumps the token; an earlier request that resolves afterwards
  // (out of order — type "SMT" then "SMTC", or select after a debounced lookup
  // already fired) sees a stale token and discards itself, so it can never write
  // a superseded fund's price back as "live" (C7).
  const priceFetchGen = useRef<Record<string, number>>({});

  // Create plan-type-specific schema
  // The pension commutation cap depends on the member's age in the start year
  const planAsOfDate = initialData?.planAsOfDate;
  const resolveStartAge = useCallback<StartAgeResolver>((personIndex, startYear) => {
    const person = persons[personIndex];
    if (!person?.dateOfBirth || !planAsOfDate) return undefined;
    return ageInPlanYear(person.dateOfBirth, planAsOfDate, startYear);
  }, [persons, planAsOfDate]);
  const schema = useMemo(() => createStep3Schema(planType as 'SINGLE' | 'JOINT', resolveStartAge), [planType, resolveStartAge]);
  // One-line "what the preset set" note per pension card, keyed "person-pension"
  const dbEnrichment = isFeatureEnabled('dbPensionEnrichment');
  /**
   * A pension that already carries enrichment (a preset, a normal pension age,
   * a lump sum, commutation, a survivor share, or a capped or fixed increase
   * rule) keeps the full card even with the switch off, so the value can be
   * seen and changed rather than silently rewritten by the plain checkbox.
   */
  const pensionIsEnriched = (p: AdditionalPensionData | undefined): boolean => !!p && (
    !!p.schemePreset || p.normalPensionAge != null || (p.automaticLumpSum ?? 0) > 0
    || (p.commutedAnnual ?? 0) > 0 || p.survivorPct != null
    || (p.indexation != null && p.indexation !== 'CPI' && p.indexation !== 'NONE')
  );
  const [presetNotes, setPresetNotes] = useState<Record<string, string>>({});
  // Cards whose More options disclosure has been auto-opened by a preset once
  const [presetOpened, setPresetOpened] = useState<Record<string, boolean>>({});

  // Initialize holdings - only include persons relevant to plan type
  const initialHoldings = useMemo((): Record<string, HoldingData[]> => planType === 'JOINT'
    ? {
        person0: initialData?.holdings?.person0 || [],
        person1: initialData?.holdings?.person1 || [],
      }
    : {
        person0: initialData?.holdings?.person0 || [],
      }, [planType, initialData?.holdings?.person0, initialData?.holdings?.person1]);

  // Drafts saved before indexation existed carry only inflationLinked → CPI / NONE
  const withIndexation = (pensions: AdditionalPensionData[] | undefined): AdditionalPensionData[] =>
    (pensions || []).map(p => ({ ...p, indexation: legacyIndexation(p) }));
  const initialPensions = useMemo((): Record<string, AdditionalPensionData[]> => planType === 'JOINT'
    ? {
        person0: withIndexation(initialData?.additionalPensions?.person0),
        person1: withIndexation(initialData?.additionalPensions?.person1),
      }
    : {
        person0: withIndexation(initialData?.additionalPensions?.person0),
      }, [planType, initialData?.additionalPensions?.person0, initialData?.additionalPensions?.person1]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<Step3InternalFormData>({
    resolver: zodResolver(schema),
    // onTouched: validate on blur once a field has been touched, then onChange
    // for the rest of the session. Catches partial edits (e.g. 0-value holdings
    // left after deleting the placeholder) before the user leaves the step.
    mode: 'onTouched',
    defaultValues: {
      holdings: initialHoldings,
      additionalPensions: initialPensions,
    },
  });

  // When a draft is loaded with pre-existing data (e.g. user revisits the
  // wizard with a 0-value holding row from a prior session), flag invalid
  // fields immediately rather than waiting for the user to click Continue.
  const didValidateOnMountRef = useRef(false);
  useEffect(() => {
    // First run only: re-validating later would re-surface errors mid-edit.
    if (didValidateOnMountRef.current) return;
    didValidateOnMountRef.current = true;
    const personKeys: ReadonlyArray<'person0' | 'person1'> = ['person0', 'person1'];
    const hasPersistedHoldings = personKeys.some(
      key => (initialHoldings[key]?.length ?? 0) > 0 || (initialPensions[key]?.length ?? 0) > 0,
    );
    if (hasPersistedHoldings) {
      // `trigger()` populates `formState.errors` for invalid fields. With
      // `mode: 'onTouched'` the subsequent edit re-validates on change,
      // clearing the error as the user fixes the value. (Switching to
      // `mode: 'onBlur'` would leave the error stale until blur — keep this
      // pair coupled.)
      trigger().catch(err => {
        console.warn('Step3 mount-time validation failed:', err);
      });
    }
  }, [initialHoldings, initialPensions, trigger]);

  // Use field arrays for each person's holdings - must be called unconditionally
  const person0FieldArray = useFieldArray({
    control,
    name: 'holdings.person0' as const,
  });

  const person1FieldArray = useFieldArray({
    control,
    name: 'holdings.person1' as const,
  });

  // This memo does NOT stabilise anything: useFieldArray returns a fresh object
  // every render, so both deps change every render and the cache never hits. It
  // is here because exhaustive-deps requires it — the rule cannot see that. The
  // consumers below are all onClick handlers, so the churn is free; do not add
  // addHolding/removeHolding to an effect's deps expecting a stable identity.
  const fieldArrays = useMemo(() => [person0FieldArray, person1FieldArray], [person0FieldArray, person1FieldArray]);

  // Use field arrays for each person's additional pensions
  const person0PensionArray = useFieldArray({
    control,
    name: 'additionalPensions.person0' as const,
  });

  const person1PensionArray = useFieldArray({
    control,
    name: 'additionalPensions.person1' as const,
  });

  const pensionArrays = [person0PensionArray, person1PensionArray];

  const handleBlur = useCallback(() => {
    const data = watch();
    onSave({ ...data, contributions });
  }, [watch, onSave, contributions]);

  const handleContributionsChange = useCallback((newContribs: Record<string, ContributionPersonData>) => {
    setContributions(newContribs);
    const data = watch();
    onSave({ ...data, contributions: newContribs });
  }, [watch, onSave]);

  // Clean up debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  /** Auto-fill holding fields from a fund metadata response */
  const applyFundToHolding = useCallback((personIndex: number, holdingIndex: number, fund: FundMetadataResponse) => {
    const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
    setValue(`${prefix}.ticker`, fund.ticker);
    setValue(`${prefix}.etfName`, fund.name);
    setValue(`${prefix}.assetClass`, mapFundAssetClass(fund.assetClass));
    if (fund.ocfPct != null) {
      setValue(`${prefix}.ocfPct`, fund.ocfPct);
    }
    // Open unit-count tracking by default — a selected ticker means we can price
    // it, so the user should only need to type their unit count (#1636 UX).
    setValue(`${prefix}.quantityTracked`, true);
    handleBlur();
  }, [setValue, handleBlur]);

  // #1531 — quantity/unit-price capture. currentValue is derived (quantity * unitPrice)
  // and kept live via setValue, mirroring the OCF/ticker auto-fill pattern above: the
  // result stays a normal editable field, never locked (guardrail #1531-1).
  const [livePriceLoading, setLivePriceLoading] = useState<Record<string, boolean>>({});
  const [livePriceFailed, setLivePriceFailed] = useState<Record<string, boolean>>({});
  // True only right after a live fetch actually succeeded for this holding —
  // never inferred from ticker presence alone. Cleared on a failed fetch or
  // a manual edit, so the "live" badge never claims freshness it can't back up.
  const [priceIsLive, setPriceIsLive] = useState<Record<string, boolean>>({});
  // Exchange + native price/currency from the last live fetch (#1636), so a
  // non-GBP holding shows where it trades and that the £ is FX-converted.
  const [livePriceInfo, setLivePriceInfo] = useState<Record<string, { exchange?: string; tradeCurrency?: string; tradePrice?: number }>>({});
  // True right after a unit count is inferred from existing total ÷ live price
  // (never persisted — purely a UI cue that the figure is a starting estimate,
  // not something the user typed). Cleared the moment they edit Units held.
  const [quantityEstimated, setQuantityEstimated] = useState<Record<string, boolean>>({});

  // #1533 — which holding's edit drawer is open (grouped read-only table + drawer redesign)
  const [editingHolding, setEditingHolding] = useState<{ personIndex: number; holdingIndex: number; mode: 'edit' | 'add' } | null>(null);
  const [bulkFetching, setBulkFetching] = useState<Record<number, boolean>>({});
  const [lastFetchedLabel, setLastFetchedLabel] = useState<Record<number, string | null>>({});

  const deriveCurrentValue = useCallback((personIndex: number, holdingIndex: number) => {
    const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
    const quantity = getValues(`${prefix}.quantity`) as number | undefined;
    const unitPrice = getValues(`${prefix}.unitPrice`) as number | undefined;
    if (typeof quantity === 'number' && quantity > 0 && typeof unitPrice === 'number' && unitPrice > 0) {
      setValue(`${prefix}.currentValue`, quantity * unitPrice);
    }
  }, [getValues, setValue]);

  const enableQuantityTracking = useCallback(async (personIndex: number, holdingIndex: number, fieldId: string) => {
    const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
    const existingValue = (getValues(`${prefix}.currentValue`) as number | undefined) || 0;
    const ticker = getValues(`${prefix}.ticker`) as string | undefined;
    const key = fieldId;

    // Where a ticker match exists, infer today's unit count from the existing
    // total ÷ live price rather than defaulting to a meaningless "1 unit at
    // £existingValue/unit" pairing — day-one precision doesn't need to be
    // exact, but starting from a real estimate beats starting from a fiction
    // the user then has to fully re-enter themselves. currentValue is left as
    // the authoritative original figure (not recomputed from quantity*price)
    // to avoid introducing rounding drift the moment tracking switches on;
    // the units/price fields remain fully editable if the estimate is off.
    if (ticker && existingValue > 0) {
      setLivePriceLoading(prev => ({ ...prev, [key]: true }));
      try {
        const live = await getLivePrice(ticker);
        if (live && live.price > 0) {
          // 2dp, not 4 — a "units held" figure implying 4-decimal-place
          // precision reads as more exact than a back-calculated estimate
          // actually is.
          const inferredQuantity = Math.round((existingValue / live.price) * 100) / 100;
          setValue(`${prefix}.quantity`, inferredQuantity);
          setValue(`${prefix}.unitPrice`, live.price);
          setValue(`${prefix}.quantityTracked`, true);
          setQuantityEstimated(prev => ({ ...prev, [key]: true }));
          setPriceIsLive(prev => ({ ...prev, [key]: true }));
          handleBlur();
          return;
        }
      } catch (error) {
        console.warn('Live price fetch failed while enabling unit tracking:', error instanceof Error ? error.message : error);
      } finally {
        setLivePriceLoading(prev => ({ ...prev, [key]: false }));
      }
    }

    // No ticker, no live price available, or the fetch failed — fall back to
    // a manual starting point the user fills in themselves.
    const unitPrice = existingValue > 0 ? existingValue : 1;
    setValue(`${prefix}.quantity`, 1);
    setValue(`${prefix}.unitPrice`, unitPrice);
    // Keep currentValue consistent with quantity * unitPrice immediately —
    // otherwise a holding that started at £0 would briefly show "Value: £0"
    // next to a derived 1 * £1 = £1 units/price pairing.
    setValue(`${prefix}.currentValue`, unitPrice);
    setValue(`${prefix}.quantityTracked`, true);
    setPriceIsLive(prev => ({ ...prev, [key]: false }));
    handleBlur();
  }, [getValues, setValue, handleBlur]);

  const removeQuantityTracking = useCallback((personIndex: number, holdingIndex: number, fieldId: string) => {
    const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
    // quantityTracked stays true — it records that tracking was ever enabled for this
    // holding, so a later re-enable is never mistaken for the BROKERAGE first-time
    // opt-in case. Only the live quantity/unitPrice pairing is cleared.
    setValue(`${prefix}.quantity`, undefined);
    setValue(`${prefix}.unitPrice`, undefined);
    setQuantityEstimated(prev => ({ ...prev, [fieldId]: false }));
    handleBlur();
  }, [setValue, handleBlur]);

  // Keyed by fieldId (stable across index shifts), NOT `${personIndex}-${holdingIndex}`:
  // an index key is inherited by whichever holding shifts into that slot after a
  // delete, so holding 1 would show the deleted holding 0's exchange/native price
  // under a false "live" badge (C6). setValue still uses the index path — only the
  // per-holding UI STATE is keyed by fieldId.
  const fetchLivePriceForHolding = useCallback(async (personIndex: number, holdingIndex: number, ticker: string, fieldId: string) => {
    const key = fieldId;
    const gen = (priceFetchGen.current[key] ?? 0) + 1;
    priceFetchGen.current[key] = gen;
    const isStale = () => priceFetchGen.current[key] !== gen;

    setLivePriceLoading(prev => ({ ...prev, [key]: true }));
    setLivePriceFailed(prev => ({ ...prev, [key]: false }));
    try {
      const result = await getLivePrice(ticker);
      if (isStale()) return;   // a newer fetch for this field superseded us
      if (result) {
        const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
        setValue(`${prefix}.unitPrice`, result.price);
        setPriceIsLive(prev => ({ ...prev, [key]: true }));
        setLivePriceInfo(prev => ({ ...prev, [key]: { exchange: result.exchange, tradeCurrency: result.tradeCurrency, tradePrice: result.tradePrice } }));
        deriveCurrentValue(personIndex, holdingIndex);
        handleBlur();
      } else {
        // Fail loud, not silent — leave the existing price untouched and tell
        // the user so they know to check/enter it manually.
        setLivePriceFailed(prev => ({ ...prev, [key]: true }));
        setPriceIsLive(prev => ({ ...prev, [key]: false }));
      }
    } catch (error) {
      console.warn('Live price fetch failed:', error instanceof Error ? error.message : error);
      if (isStale()) return;
      setLivePriceFailed(prev => ({ ...prev, [key]: true }));
      setPriceIsLive(prev => ({ ...prev, [key]: false }));
    } finally {
      if (!isStale()) setLivePriceLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [setValue, deriveCurrentValue, handleBlur]);

  const addHolding = useCallback((personIndex: number) => {
    const newHolding: HoldingData = {
      holdingId: crypto.randomUUID(),
      accountType: 'SIPP',
      etfName: '',
      ticker: '',
      assetClass: 'EQUITY',
      currentValue: 0,
    };
    fieldArrays[personIndex].append(newHolding);
  }, [fieldArrays]);

  const removeHolding = useCallback((personIndex: number, holdingIndex: number, fieldId: string) => {
    fieldArrays[personIndex].remove(holdingIndex);
    // Clean up all per-holding state (keyed by fieldId, stable across index
    // shifts) so a deleted holding leaves nothing behind for the next holding to
    // inherit.
    const dropKey = (prev: Record<string, unknown>) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    };
    setTickerLookups(prev => dropKey(prev) as Record<string, TickerLookupState>);
    setLivePriceLoading(prev => dropKey(prev) as Record<string, boolean>);
    setLivePriceFailed(prev => dropKey(prev) as Record<string, boolean>);
    setPriceIsLive(prev => dropKey(prev) as Record<string, boolean>);
    setLivePriceInfo(prev => dropKey(prev) as Record<string, { exchange?: string; tradeCurrency?: string; tradePrice?: number }>);
    setQuantityEstimated(prev => dropKey(prev) as Record<string, boolean>);
    delete priceFetchGen.current[fieldId];
    // Defer save to next tick so RHF state reflects the removal
    setTimeout(handleBlur, 0);
  }, [fieldArrays, handleBlur]);

  const handleRowClick = useCallback((personIndex: number, holdingIndex: number) => {
    setEditingHolding({ personIndex, holdingIndex, mode: 'edit' });
  }, []);

  const handleAddInvestment = useCallback((personIndex: number) => {
    const newIndex = fieldArrays[personIndex].fields.length;
    addHolding(personIndex);
    setEditingHolding({ personIndex, holdingIndex: newIndex, mode: 'add' });
  }, [fieldArrays, addHolding]);

  // Keeps the drawer open and moves straight to a fresh blank holding — lets
  // someone entering several holdings in one sitting stay in the drawer
  // instead of open/close/reopen per holding. If the current one was never
  // touched, reuse it rather than leaving a second empty holding behind.
  const handleSaveAndAddAnother = useCallback((personIndex: number, holdingIndex: number) => {
    const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
    const etfName = (getValues(`${prefix}.etfName`) as string | undefined)?.trim();
    const currentValue = getValues(`${prefix}.currentValue`) as number | undefined;
    if (!etfName && !currentValue) {
      return;
    }
    const newIndex = fieldArrays[personIndex].fields.length;
    addHolding(personIndex);
    setEditingHolding({ personIndex, holdingIndex: newIndex, mode: 'add' });
  }, [getValues, fieldArrays, addHolding]);

  const handleDrawerClose = useCallback(() => {
    if (editingHolding?.mode === 'add') {
      const prefix = `holdings.person${editingHolding.personIndex}.${editingHolding.holdingIndex}` as const;
      const etfName = (getValues(`${prefix}.etfName`) as string | undefined)?.trim();
      const currentValue = getValues(`${prefix}.currentValue`) as number | undefined;
      // Untouched placeholder from "+ Add Investment" — drop it rather than
      // leaving a 0-value row. A partially-filled holding (a name, or a picked
      // ticker) is deliberately KEPT so the user can finish it later; the server
      // repairs an unfinished 0-value holding on read (#1637), so keeping it
      // never breaks a projection.
      if (!etfName && !currentValue) {
        removeHolding(editingHolding.personIndex, editingHolding.holdingIndex, fieldArrays[editingHolding.personIndex].fields[editingHolding.holdingIndex]?.id ?? '');
      }
    }
    setEditingHolding(null);
  }, [editingHolding, getValues, fieldArrays, removeHolding]);

  const handleDrawerDelete = useCallback(() => {
    if (!editingHolding) return;
    const fieldId = fieldArrays[editingHolding.personIndex].fields[editingHolding.holdingIndex]?.id ?? '';
    removeHolding(editingHolding.personIndex, editingHolding.holdingIndex, fieldId);
    setEditingHolding(null);
  }, [editingHolding, fieldArrays, removeHolding]);

  const handleFetchAllPrices = useCallback(async (personIndex: number) => {
    const personHoldings = (getValues(`holdings.person${personIndex}`) as HoldingData[] | undefined) || [];
    const fetchable = personHoldings
      .map((h, idx) => ({ h, idx }))
      .filter(({ h }) => h.quantityTracked && h.quantity != null && h.unitPrice != null && h.ticker);

    if (fetchable.length === 0) return;

    setBulkFetching(prev => ({ ...prev, [personIndex]: true }));
    await Promise.all(fetchable.map(({ h, idx }) => {
      const fieldId = fieldArrays[personIndex].fields[idx]?.id ?? '';
      return fetchLivePriceForHolding(personIndex, idx, h.ticker!, fieldId);
    }));
    setBulkFetching(prev => ({ ...prev, [personIndex]: false }));
    const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    setLastFetchedLabel(prev => ({ ...prev, [personIndex]: `Updated at ${time} today · prices delayed by at least 15 minutes` }));
  }, [getValues, fetchLivePriceForHolding, fieldArrays]);

  const handleCostBasisChange = useCallback((personIndex: number, holdingIndex: number, total: number | undefined) => {
    setValue(`holdings.person${personIndex}.${holdingIndex}.costBasis`, total);
    handleBlur();
  }, [setValue, handleBlur]);

  /** Called when user selects an item from the autocomplete dropdown */
  const handleTickerSelect = useCallback((fieldId: string, personIndex: number, holdingIndex: number, fund: FundMetadataResponse) => {
    // Cancel any pending debounced text-lookup for this field: an explicit
    // selection is the user's final word, and a queued fuzzy lookup resolving
    // afterwards would otherwise overwrite the chosen fund (C7).
    if (debounceTimers.current[fieldId]) {
      clearTimeout(debounceTimers.current[fieldId]);
      delete debounceTimers.current[fieldId];
    }
    setTickerLookups(prev => ({
      ...prev,
      [fieldId]: { status: 'found', fundName: fund.name },
    }));
    applyFundToHolding(personIndex, holdingIndex, fund);
    void fetchLivePriceForHolding(personIndex, holdingIndex, fund.ticker, fieldId);   // pre-fill today's price (#1637 UX)
  }, [applyFundToHolding, fetchLivePriceForHolding]);

  /**
   * Fallback exact lookup via Yahoo Finance.
   * Triggered when user changes ticker text and doesn't select from dropdown.
   * Debounced at 500ms — only fires after user stops typing.
   */
  const handleTickerLookup = useCallback((fieldId: string, personIndex: number, holdingIndex: number, ticker: string) => {
    const key = fieldId;

    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }

    if (!ticker || ticker.trim().length < 2) {
      setTickerLookups(prev => ({ ...prev, [key]: { status: 'idle' } }));
      return;
    }

    setTickerLookups(prev => ({ ...prev, [key]: { status: 'loading' } }));

    const gen = (priceFetchGen.current[key] ?? 0) + 1;
    priceFetchGen.current[key] = gen;
    debounceTimers.current[key] = setTimeout(async () => {
      try {
        const fund = await lookupFundTicker(ticker.trim());
        // A newer keystroke or an explicit selection bumped the token while we
        // were awaiting — discard this fuzzy result rather than overwrite the
        // fund the user has since chosen or narrowed to (C7).
        if (priceFetchGen.current[key] !== gen) return;
        if (!fund) {
          setTickerLookups(prev => ({ ...prev, [key]: { status: 'not-found' } }));
          return;
        }

        applyFundToHolding(personIndex, holdingIndex, fund);
        void fetchLivePriceForHolding(personIndex, holdingIndex, fund.ticker, fieldId);   // pre-fill today's price (#1637 UX)
        setTickerLookups(prev => ({
          ...prev,
          [key]: { status: 'found', fundName: fund.name },
        }));
      } catch (error) {
        console.warn(`Ticker lookup failed for "${ticker}":`, error instanceof Error ? error.message : error);
        if (priceFetchGen.current[key] !== gen) return;
        setTickerLookups(prev => ({ ...prev, [key]: { status: 'error' } }));
      }
    }, 500);
  }, [applyFundToHolding, fetchLivePriceForHolding]);

  const onInvalid = useCallback((formErrors: FieldErrors<Step3InternalFormData>) => {
    // Auto-switch to the first person tab that has validation errors
    // and auto-expand collapsed sections containing errors
    for (let i = 0; i < numPersons; i++) {
      const personKey = `person${i}`;
      const holdingErrors = formErrors.holdings?.[personKey];
      const hasHoldingErrors = !!holdingErrors;
      const hasPensionErrors = !!formErrors.additionalPensions?.[personKey];
      if (hasHoldingErrors || hasPensionErrors) {
        setActivePersonIndex(i);
        if (hasPensionErrors) {
          setPensionExpanded(prev => ({ ...prev, [i]: true }));
        }
        // The grouped table is read-only — an invalid holding's error only
        // shows inside its drawer, so open it here rather than leaving Next
        // silently do nothing with no visible explanation.
        if (hasHoldingErrors && Array.isArray(holdingErrors)) {
          const firstInvalidIndex = holdingErrors.findIndex((e: unknown) => !!e);
          if (firstInvalidIndex !== -1) {
            setEditingHolding({ personIndex: i, holdingIndex: firstInvalidIndex, mode: 'edit' });
          }
        }
        return;
      }
    }
  }, [numPersons, setEditingHolding]);

  const onSubmit = (data: Step3InternalFormData) => {
    const normalise = (pensions: AdditionalPensionData[] | undefined) =>
      (pensions ?? []).map(p => normalisePensionForSave(p, planType as 'SINGLE' | 'JOINT'));
    const filteredData = planType === 'SINGLE'
      ? {
          holdings: { person0: data.holdings.person0 },
          additionalPensions: data.additionalPensions?.person0?.length
            ? { person0: normalise(data.additionalPensions.person0) }
            : undefined,
          contributions,
        }
      : {
          holdings: data.holdings,
          additionalPensions: {
            person0: normalise(data.additionalPensions?.person0),
            person1: normalise(data.additionalPensions?.person1),
          },
          contributions,
        };
    onNext(filteredData as Step3FormData);
  };

  const addPension = (personIndex: number) => {
    const newPension: AdditionalPensionData = {
      pensionId: crypto.randomUUID(),
      name: '',
      annualAmount: 0,
      startYear: 1,
      inflationLinked: true,
      indexation: 'CPI',
      commutationFactor: DEFAULT_COMMUTATION_FACTOR,
    };
    pensionArrays[personIndex].append(newPension);
    setPensionExpanded(prev => ({ ...prev, [personIndex]: true }));
  };

  /** Scheme select: fill the preset's fields (all still editable) and say what was set. */
  const applyPresetToPension = (personIndex: number, pensionIndex: number, key: string) => {
    const noteKey = `${personIndex}-${pensionIndex}`;
    const preset = findPreset(key);
    if (!preset) {
      setPresetNotes(prev => ({ ...prev, [noteKey]: '' }));
      return;
    }
    const isJoint = planType === 'JOINT';
    const spa = persons[personIndex]?.statePensionAge ?? 67;
    const fill = applyPreset(preset, spa);
    const base = `additionalPensions.person${personIndex}.${pensionIndex}` as const;
    const opts = { shouldDirty: true, shouldValidate: true };
    if (fill.indexation) setValue(`${base}.indexation`, fill.indexation, opts);
    if (fill.normalPensionAge != null) setValue(`${base}.normalPensionAge`, fill.normalPensionAge, opts);
    if (fill.earlyReductionPctPerYear != null) setValue(`${base}.earlyReductionPctPerYear`, fill.earlyReductionPctPerYear, opts);
    if (fill.lateIncreasePctPerYear != null) setValue(`${base}.lateIncreasePctPerYear`, fill.lateIncreasePctPerYear, opts);
    if (isJoint && fill.survivorPct != null) setValue(`${base}.survivorPct`, fill.survivorPct, opts);
    setPresetNotes(prev => ({ ...prev, [noteKey]: describePresetFill(preset, spa, { joint: isJoint }) }));
    // Open More options the first time a preset fills it in, so the user can
    // see and check the values against a benefit statement rather than take
    // the note's word for it. Only the first time: a user who closes it again
    // has said they are happy, and reopening on every change would fight them.
    setPresetOpened(prev => (prev[noteKey] ? prev : { ...prev, [noteKey]: true }));
  };

  const removePension = (personIndex: number, pensionIndex: number) => {
    pensionArrays[personIndex].remove(pensionIndex);
    // Defer save to next tick so RHF state reflects the removal
    setTimeout(handleBlur, 0);
  };

  const togglePensionSection = (personIndex: number) => {
    setPensionExpanded(prev => ({ ...prev, [personIndex]: !prev[personIndex] }));
  };

  // Watch all holdings to trigger re-renders when assetClass or bondDetails change
  const watchedHoldings = watch('holdings');

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Add your retirement savings</h2>
          <TourButton tour={WIZARD_STEP3_TOUR} variant="text" />
        </div>
        <p>Add your pension (SIPP), ISA, and brokerage investments — optional if you have other income sources like State Pension or DB pensions</p>
      </div>

      <details className="account-type-disclosure" open={!localStorage.getItem('tour-seen-step3-account-types') || undefined} onToggle={() => localStorage.setItem('tour-seen-step3-account-types', 'true')}>
        <summary>Which account type should I pick?</summary>
        <p><strong>SIPP (Self-Invested Personal Pension)</strong> — Your pension pot. Withdrawals are taxed as income, but 25% can be taken tax-free (capped at £268,275). Check your pension provider — it will say &quot;SIPP&quot;, &quot;personal pension&quot;, or &quot;drawdown&quot;.</p>
        <p><strong>ISA (Individual Savings Account)</strong> — A tax-free savings wrapper. Everything you withdraw is completely free of income tax and Capital Gains Tax.</p>
        <p><strong>Brokerage / GIA (General Investment Account)</strong> — A standard investment account with no tax wrapper. Profits on sale may trigger Capital Gains Tax above the £3,000 annual exemption.</p>
        <p><strong>Not sure?</strong> Check your account statement or log in to your provider — the account type is always shown prominently.</p>
      </details>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form step-form-wide">
        {/* Tabs for JOINT plans */}
        {planType === 'JOINT' && (
          <div className="holdings-tabs">
            {Array.from({ length: numPersons }, (_, i) => {
              const personKey = `person${i}`;
              const hasError = !!errors.holdings?.[personKey as keyof typeof errors.holdings];
              return (
                <button
                  key={i}
                  type="button"
                  className={`holdings-tab ${activePersonIndex === i ? 'active' : ''} ${hasError ? 'holdings-tab--error' : ''}`}
                  onClick={() => setActivePersonIndex(i)}
                  aria-invalid={hasError || undefined}
                >
                  {persons[i]?.fullName || `Person ${i + 1}`}'s Investments
                  {hasError && <span className="tab-error-badge" aria-label="Has validation errors">!</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Holdings table for active person */}
        {Array.from({ length: numPersons }, (_, personIndex) => (
          <div
            key={personIndex}
            className={`holdings-container ${planType === 'SINGLE' || personIndex === activePersonIndex ? 'active' : 'hidden'}`}
          >
            {planType === 'SINGLE' && (
              <h3 className="person-holdings-header">Your Investments</h3>
            )}

            {fieldArrays[personIndex].fields.length === 0 ? (
              <div className="empty-state">
                <p>No investments added</p>
                <p className="empty-state-hint">This is fine if you have other income sources such as State Pension, DB pensions, or property downsizing.</p>
              </div>
            ) : (
              <HoldingsTable
                holdings={(watchedHoldings as Record<string, HoldingData[]>)?.[`person${personIndex}`] || []}
                onRowClick={(holdingIndex) => handleRowClick(personIndex, holdingIndex)}
                onAddInvestment={() => handleAddInvestment(personIndex)}
                onFetchAllPrices={() => handleFetchAllPrices(personIndex)}
                fetchingPrices={!!bulkFetching[personIndex]}
                fetchablePriceCount={
                  ((watchedHoldings as Record<string, HoldingData[]>)?.[`person${personIndex}`] || [])
                    .filter(h => h.quantityTracked && h.quantity != null && h.unitPrice != null && h.ticker).length
                }
                lastFetchedLabel={lastFetchedLabel[personIndex] ?? null}
              />
            )}

            {/* OCF disclaimer */}
            {fieldArrays[personIndex].fields.length > 0 && (
              <p className="ocf-disclaimer">
                OCF (%) is auto-filled where available. You can edit or enter values manually — check your fund's Key Information Document (KID) for the exact figure. OCF is deducted from projected investment returns.
              </p>
            )}

            {/* Empty state still needs its own way in — HoldingsTable's toolbar only
                renders once a holding already exists. */}
            {fieldArrays[personIndex].fields.length === 0 && (
              <button
                type="button"
                className="add-holding-btn"
                onClick={() => handleAddInvestment(personIndex)}
              >
                + Add Investment
              </button>
            )}

            {/* Per-holding field errors are shown inline; no array-level error needed */}

            {/* Additional Pensions Section */}
            <div className="additional-pensions-section">
              <button
                type="button"
                className="additional-pensions-toggle"
                onClick={() => togglePensionSection(personIndex)}
                aria-expanded={!!pensionExpanded[personIndex]}
              >
                <span className="toggle-icon">{pensionExpanded[personIndex] ? '\u2212' : '+'}</span>
                <span>Guaranteed pension income (e.g. NHS, teachers, civil service)</span>
                {pensionArrays[personIndex].fields.length > 0 && (
                  <span className="pension-count">{pensionArrays[personIndex].fields.length}</span>
                )}
              </button>

              {pensionExpanded[personIndex] && (
                <div className="additional-pensions-content">
                  {pensionArrays[personIndex].fields.length === 0 ? (
                    <p className="pension-empty-hint">
                      Add any defined-benefit or workplace pensions that will provide guaranteed income in retirement.
                    </p>
                  ) : (
                    <div className="pensions-list">
                      {pensionArrays[personIndex].fields.map((field, pensionIndex) => (
                        <div key={field.id} className="pension-card">
                          <div className="pension-card-fields">
                            {(dbEnrichment || pensionIsEnriched(watch(`additionalPensions.person${personIndex}.${pensionIndex}`))) && (
                            <div className="pension-field">
                              <label htmlFor={`pension-scheme-${personIndex}-${pensionIndex}`} className="pension-label">Scheme</label>
                              <select
                                id={`pension-scheme-${personIndex}-${pensionIndex}`}
                                className="form-input form-input-sm"
                                {...register(`additionalPensions.person${personIndex}.${pensionIndex}.schemePreset`)}
                                onChange={(e) => {
                                  void register(`additionalPensions.person${personIndex}.${pensionIndex}.schemePreset`).onChange(e);
                                  applyPresetToPension(personIndex, pensionIndex, e.target.value);
                                  handleBlur();
                                }}
                              >
                                <option value="">Choose a scheme…</option>
                                {DB_PENSION_PRESETS.map(preset => (
                                  <option key={preset.key} value={preset.key}>{preset.label}</option>
                                ))}
                              </select>
                            </div>
                            )}
                            <div className="pension-field">
                              <label htmlFor={`pension-name-${personIndex}-${pensionIndex}`} className="pension-label">Pension Name</label>
                              <input
                                id={`pension-name-${personIndex}-${pensionIndex}`}
                                type="text"
                                className={`form-input form-input-sm ${(errors.additionalPensions as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[pensionIndex]?.name ? 'error' : ''}`}
                                placeholder="e.g., NHS Pension"
                                {...register(`additionalPensions.person${personIndex}.${pensionIndex}.name`)}
                                onBlur={handleBlur}
                              />
                              {(errors.additionalPensions as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[pensionIndex]?.name && (
                                <span className="field-error-inline">Name required</span>
                              )}
                            </div>
                            <div className="pension-field">
                              <label htmlFor={`pension-amount-${personIndex}-${pensionIndex}`} className="pension-label">Annual Amount ({'\u00A3'})</label>
                              <CurrencyInput
                                id={`pension-amount-${personIndex}-${pensionIndex}`}
                                name={`additionalPensions.person${personIndex}.${pensionIndex}.annualAmount`}
                                control={control}
                                onBlur={handleBlur}
                                showZeroAsEmpty
                              />
                              {(errors.additionalPensions as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[pensionIndex]?.annualAmount && (
                                <span className="field-error-inline">Amount required</span>
                              )}
                            </div>
                            <div className="pension-field">
                              <label htmlFor={`pension-start-${personIndex}-${pensionIndex}`} className="pension-label">Starts Year</label>
                              <input
                                id={`pension-start-${personIndex}-${pensionIndex}`}
                                type="number"
                                className={`form-input form-input-sm ${(errors.additionalPensions as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[pensionIndex]?.startYear ? 'error' : ''}`}
                                min="1"
                                {...register(`additionalPensions.person${personIndex}.${pensionIndex}.startYear`, { valueAsNumber: true })}
                                onBlur={handleBlur}
                              />
                              {(errors.additionalPensions as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[pensionIndex]?.startYear && (
                                <span className="field-error-inline">Start year required</span>
                              )}
                              {initialData?.planAsOfDate && (() => {
                                const yr = watch(`additionalPensions.person${personIndex}.${pensionIndex}.startYear`);
                                if (!yr) return null;
                                const age = resolveStartAge(personIndex, yr);
                                const label = formatYearAge({
                                  planYear: yr,
                                  calendarYear: planYearToCalendar(yr, initialData.planAsOfDate),
                                  people: age == null ? undefined : [{ name: persons[personIndex]?.fullName || `Person ${personIndex + 1}`, age }],
                                }, 'inline');
                                return <span className="field-context-hint">{label}</span>;
                              })()}
                            </div>
                            {(dbEnrichment || pensionIsEnriched(watch(`additionalPensions.person${personIndex}.${pensionIndex}`))) ? (
                            <PensionDetailFields
                              personIndex={personIndex}
                              pensionIndex={pensionIndex}
                              planType={planType as 'SINGLE' | 'JOINT'}
                              person={persons[personIndex]}
                              planAsOfDate={initialData?.planAsOfDate}
                              register={register}
                              control={control}
                              watch={watch}
                              fieldErrors={(errors.additionalPensions as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[pensionIndex]}
                              onFieldBlur={handleBlur}
                              presetNote={presetNotes[`${personIndex}-${pensionIndex}`] ?? ''}
                              defaultMoreOptionsOpen={presetOpened[`${personIndex}-${pensionIndex}`] ?? false}
                            />
                            ) : (
                            <div className="pension-field pension-field-checkbox">
                              <label className="pension-checkbox-label">
                                <input
                                  type="checkbox"
                                  {...register(`additionalPensions.person${personIndex}.${pensionIndex}.inflationLinked`)}
                                  onChange={(e) => {
                                    void register(`additionalPensions.person${personIndex}.${pensionIndex}.inflationLinked`).onChange(e);
                                    // The save derives inflationLinked from indexation, so the
                                    // plain checkbox has to move indexation too or it would be ignored.
                                    setValue(`additionalPensions.person${personIndex}.${pensionIndex}.indexation`, e.target.checked ? 'CPI' : 'NONE');
                                    handleBlur();
                                  }}
                                />
                                {' '}Inflation-linked
                              </label>
                            </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn-delete pension-delete"
                            onClick={() => removePension(personIndex, pensionIndex)}
                            title="Remove pension"
                          >
                            {'\uD83D\uDDD1\uFE0F'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="add-holding-btn"
                    onClick={() => addPension(personIndex)}
                  >
                    + Add Pension
                  </button>
                </div>
              )}
            </div>

            {/* Pre-Retirement Contributions (per person) */}
            <ContributionsSection
              persons={[persons[personIndex]]}
              contributions={contributions[`person${personIndex}`] ? { person0: contributions[`person${personIndex}`] } : {}}
              onChange={(updated) => {
                const key = `person${personIndex}`;
                if (updated.person0) {
                  handleContributionsChange({ ...contributions, [key]: updated.person0 });
                }
              }}
            />
          </div>
        ))}

        {editingHolding && (() => {
          const { personIndex, holdingIndex, mode } = editingHolding;
          const fieldId = fieldArrays[personIndex].fields[holdingIndex]?.id ?? '';
          const holdingValues = (watchedHoldings as Record<string, HoldingData[]>)?.[`person${personIndex}`]?.[holdingIndex];
          const lookupState = tickerLookups[fieldId];
          const etfNameError = (errors.holdings as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[holdingIndex]?.etfName?.message;
          const currentValueError = (errors.holdings as PersonKeyedFieldErrors | undefined)?.[`person${personIndex}`]?.[holdingIndex]?.currentValue?.message;
          // Per-holding UI state is keyed by the stable fieldId, not the index —
          // otherwise it survives a delete and re-attaches to whatever holding
          // shifts into the slot (C6).
          const stateKey = fieldId;
          return (
            <HoldingEditDrawer
              isOpen
              mode={mode}
              personIndex={personIndex}
              holdingIndex={holdingIndex}
              fieldId={fieldId}
              control={control}
              register={register}
              setValue={setValue}
              getValues={getValues}
              holdingValues={holdingValues}
              etfNameError={etfNameError}
              currentValueError={currentValueError}
              lookupStatus={lookupState?.status}
              onTickerChange={(val) => {
                setValue(`holdings.person${personIndex}.${holdingIndex}.ticker`, val);
                handleTickerLookup(fieldId, personIndex, holdingIndex, val);
              }}
              onTickerSelect={(fund) => handleTickerSelect(fieldId, personIndex, holdingIndex, fund)}
              onTickerBlur={handleBlur}
              onFieldBlur={handleBlur}
              onClose={handleDrawerClose}
              onDelete={handleDrawerDelete}
              onSaveAndAddAnother={() => handleSaveAndAddAnother(personIndex, holdingIndex)}
              livePriceLoading={!!livePriceLoading[stateKey]}
              livePriceFailed={!!livePriceFailed[stateKey]}
              priceIsLive={!!priceIsLive[stateKey]}
              livePriceInfo={livePriceInfo[stateKey]}
              quantityEstimated={!!quantityEstimated[stateKey]}
              onEnableQuantityTracking={() => enableQuantityTracking(personIndex, holdingIndex, fieldId)}
              onRemoveQuantityTracking={() => removeQuantityTracking(personIndex, holdingIndex, fieldId)}
              onFetchLivePrice={() => holdingValues?.ticker && fetchLivePriceForHolding(personIndex, holdingIndex, holdingValues.ticker, fieldId)}
              onQuantityFieldChange={() => {
                setQuantityEstimated(prev => ({ ...prev, [stateKey]: false }));
                deriveCurrentValue(personIndex, holdingIndex);
              }}
              onUnitPriceFieldChange={() => {
                setPriceIsLive(prev => ({ ...prev, [stateKey]: false }));
                deriveCurrentValue(personIndex, holdingIndex);
              }}
              onCostBasisChange={(total) => handleCostBasisChange(personIndex, holdingIndex, total)}
            />
          );
        })()}

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit, onInvalid)()}>
            Next: Property (Optional)
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step3Holdings;
